import { useEffect } from 'react';
import { gql, useQuery } from '@apollo/client';
import isEmpty from 'lodash/isEmpty';
import { useRideStore, useUserStore } from '../../stores';

/* ============================================
   GRAPHQL QUERY DEFINITIONS
   ============================================ */

const GET_ALL_RIDES = gql`
  query GetAllRides {
    getAllRides {
      id
      riderId
      riderName
      riderProfilePicture
      phoneNumber
      passengers
      pickUp
      dropOff
      pickUpTime
      date
      bidAmount
      waitingTime
      queuePosition
      tripDistanceMeters
      tripDurationSeconds
      tripEstimateSource
      driverId
      driverName
      isRequested
      isAccepted
      isCanceled
      isCompleted
      isExpired
      canceledBy
      completedAt
      expiredAt
      createdAt
      updatedAt
    }
  }
`;

const GET_SINGLE_RIDE = gql`
  query GetRide($id: ID!) {
    getRide(id: $id) {
      id
      riderId
      riderName
      riderProfilePicture
      phoneNumber
      passengers
      pickUp
      pickUpLat
      pickUpLng
      dropOff
      dropOffLat
      dropOffLng
      pickUpTime
      date
      bidAmount
      waitingTime
      queuePosition
      tripDistanceMeters
      tripDurationSeconds
      tripEstimateSource
      requestedDriverIds
      driverId
      driverName
      isRequested
      isAccepted
      isCanceled
      isCompleted
      isExpired
      canceledBy
      completedAt
      expiredAt
      createdAt
      updatedAt
    }
  }
`;

const GET_ACTIVE_RIDES = gql`
  query GetActiveRides($driverId: Int) {
    getActiveRides(driverId: $driverId) {
      id
      riderId
      riderName
      riderProfilePicture
      phoneNumber
      passengers
      pickUp
      pickUpLat
      pickUpLng
      dropOff
      dropOffLat
      dropOffLng
      pickUpTime
      date
      bidAmount
      waitingTime
      queuePosition
      tripDistanceMeters
      tripDurationSeconds
      tripEstimateSource
      requestedDriverIds
      driverId
      driverName
      isRequested
      isAccepted
      isCanceled
      isCompleted
      isExpired
      createdAt
      updatedAt
    }
  }
`;

const GET_ACCEPTED_RIDES = gql`
  query GetAcceptedRides($driverId: ID!) {
    getAcceptedRides(driverId: $driverId) {
      id
      riderId
      riderName
      riderProfilePicture
      phoneNumber
      passengers
      pickUp
      pickUpLat
      pickUpLng
      dropOff
      dropOffLat
      dropOffLng
      pickUpTime
      date
      bidAmount
      waitingTime
      queuePosition
      tripDistanceMeters
      tripDurationSeconds
      tripEstimateSource
      driverId
      driverName
      isRequested
      isAccepted
      isCanceled
      isCompleted
      isExpired
      canceledBy
      completedAt
      expiredAt
      createdAt
      updatedAt
    }
  }
`;

/* ============================================
   QUERY HOOKS
   ============================================ */

// Get all rides
export const useAllRides = () => {
  const {
    data: allRidesData,
    loading: loadingAllRides,
    error: errorAllRides,
    refetch: refetchAllRides,
  } = useQuery(GET_ALL_RIDES);

  return { allRidesData, loadingAllRides, errorAllRides, refetchAllRides };
};

// Get a single ride
export const useSingleRide = (rideId) => {
  const { setCurrentRide, setCurrentDriver } = useRideStore();

  const {
    data: rideData,
    loading: loadingRide,
    error: errorRide,
    refetch: refetchRide,
  } = useQuery(GET_SINGLE_RIDE, {
    variables: { id: rideId },
    skip: isEmpty(rideId),
    onCompleted: (data) => {
      if (data?.getRide) {
        if (data.getRide.driverId && data.getRide.driverName) {
          setCurrentDriver(data.getRide.driverId, data.getRide.driverName);
        }
      }
    },
  });

  return { rideData, loadingRide, errorRide, refetchRide };
};

// Get all active rides
export const useActiveRides = (driverId) => {
  const { updateActiveRides } = useRideStore();

  const {
    data: activeRidesData,
    loading: loadingActiveRides,
    error: errorActiveRides,
    refetch: refetchActiveRides,
  } = useQuery(GET_ACTIVE_RIDES, {
    variables: { driverId: driverId ? parseInt(driverId, 10) : null },
  });

  useEffect(() => {
    if (activeRidesData?.getActiveRides) {
      updateActiveRides(activeRidesData.getActiveRides);
    }
  }, [activeRidesData, updateActiveRides]);

  return {
    activeRidesData,
    loadingActiveRides,
    errorActiveRides,
    refetchActiveRides,
  };
};

// Get all accepted rides
export const useAcceptedRides = () => {
  const { updateAcceptedRides } = useRideStore();
  const { userId } = useUserStore();

  const {
    data: acceptedRidesData,
    loading: loadingAcceptedRides,
    error: errorAcceptedRides,
    refetch: refetchAcceptedRides,
  } = useQuery(GET_ACCEPTED_RIDES, {
    variables: { driverId: userId },
    skip: !userId,
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (acceptedRidesData?.getAcceptedRides) {
      updateAcceptedRides(acceptedRidesData.getAcceptedRides);
    }
  }, [acceptedRidesData, updateAcceptedRides]);

  return {
    acceptedRidesData,
    loadingAcceptedRides,
    errorAcceptedRides,
    refetchAcceptedRides,
  };
};
